/*
 * /auth/attachment-types (page)
 */
import {
	Badge,
	Button,
	cn,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	InputFormField,
	PageHeader,
	PageWithEffect,
	TextareaFormField,
	toast,
} from "@emr/react-ui"
import type { AttachmentType } from "@emr/db/client"
import { createFileRoute } from "@tanstack/react-router"
import {
	CheckCircle,
	FileText,
	Loader2,
	Pencil,
	Plus,
	Settings,
	ToggleLeft,
	ToggleRight,
	Upload,
	XCircle,
} from "lucide-react"
import { useRef, useState } from "react"
import { z } from "zod"
import { trpc } from "../../trpc"
import { adminPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/auth/attachment-types")({
	component: AttachmentTypesPage,
	loader: adminPageLoader,
})

const typeSchema = z.object({
	name: z.string().min(1, "Name is required"),
	description: z.string().optional(),
})
type TypeValues = z.infer<typeof typeSchema>

/* ─── Page ───────────────────────────────────────────────────────────────── */

function AttachmentTypesPage() {
	const [editType, setEditType] = useState<AttachmentType | null>(null)
	const [createOpen, setCreateOpen] = useState(false)

	const { data, isLoading, refetch } = trpc.attachmentType.getAllByFilter.useQuery({})

	const types = data ?? []

	const create = trpc.attachmentType.create.useMutation({
		onSuccess: () => { toast.success("Type created"); refetch(); setCreateOpen(false) },
		onError: (e) => toast.error(e.message),
	})
	const update = trpc.attachmentType.update.useMutation({
		onSuccess: () => { toast.success("Type updated"); refetch(); setEditType(null) },
		onError: (e) => toast.error(e.message),
	})

	function toggleActive(t: AttachmentType) {
		update.mutate({ id: t.id, active: !t.active })
	}

	return (
		<PageWithEffect effect="aurora" centerVertical={false}>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader
					title="Attachment Types"
					right={{
						Component: (
							<Button size="sm" onClick={() => setCreateOpen(true)}>
								<Plus className="mr-1.5 size-4" />
								New Type
							</Button>
						),
					}}
				/>

				{isLoading ? (
					<div className="flex items-center justify-center py-16">
						<Loader2 className="size-6 animate-spin text-muted-foreground" />
					</div>
				) : types.length === 0 ? (
					<p className="py-12 text-center text-sm text-muted-foreground">
						No attachment types yet. Create one to start categorising uploads.
					</p>
				) : (
					<div className="w-full overflow-x-auto rounded-lg border">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b bg-muted/40 text-left">
									<th className="px-4 py-3 font-medium">Name</th>
									<th className="px-4 py-3 font-medium">Description</th>
									<th className="px-4 py-3 font-medium">PDF Template</th>
									<th className="px-4 py-3 font-medium">Status</th>
									<th className="px-4 py-3 font-medium" />
								</tr>
							</thead>
							<tbody>
								{types.map((t) => (
									<tr key={t.id} className="border-b last:border-0 hover:bg-muted/20">
										<td className="px-4 py-3 font-medium">{t.name}</td>
										<td className="px-4 py-3 text-muted-foreground">
											{t.description ?? "—"}
										</td>
										<td className="px-4 py-3">
											{(t as any).pdfTemplateS3Key ? (
												<div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
													<FileText className="size-3.5" />
													Template uploaded
												</div>
											) : (
												<span className="text-xs text-muted-foreground opacity-50">—</span>
											)}
										</td>
										<td className="px-4 py-3">
											<Badge variant={t.active ? "secondary" : "outline"}>
												{t.active ? "Active" : "Inactive"}
											</Badge>
										</td>
										<td className="px-4 py-3 text-right">
											<div className="flex items-center justify-end gap-1">
												<Button
													variant="ghost"
													size="icon"
													className="size-8"
													title={t.active ? "Deactivate" : "Activate"}
													onClick={() => toggleActive(t)}
													disabled={update.isPending}
												>
													{t.active
														? <ToggleRight className="size-4 text-green-600" />
														: <ToggleLeft className="size-4 text-muted-foreground" />}
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="size-8"
													title="Edit"
													onClick={() => setEditType(t)}
												>
													<Pencil className="size-4" />
												</Button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Container>

			{/* Create dialog */}
			<Dialog open={createOpen} onOpenChange={setCreateOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>New Attachment Type</DialogTitle>
					</DialogHeader>
					<Form<TypeValues>
						schema={typeSchema}
						onSubmit={async (v) => {
							try {
								await create.mutateAsync({ name: v.name, description: v.description ?? null })
							} catch (e: any) {
								return e?.message ?? "An error occurred"
							}
						}}
						resetOnSuccess
						className="flex flex-col gap-4"
					>
						<InputFormField name="name" label="Name" placeholder="e.g. Medical Report" required />
						<TextareaFormField name="description" label="Description" placeholder="Optional description" />
						<DialogFooter>
							<Button type="submit" disabled={create.isPending}>
								{create.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
								Create
							</Button>
						</DialogFooter>
					</Form>
				</DialogContent>
			</Dialog>

			{/* Edit dialog */}
			{editType && (
				<EditDialog
					type={editType}
					onOpenChange={(o) => !o && setEditType(null)}
					onSaved={() => { refetch(); setEditType(null) }}
				/>
			)}
		</PageWithEffect>
	)
}

/* ─── Edit Dialog (tabbed: Info + PDF Template) ──────────────────────────── */

type EditTab = "info" | "pdf"

function EditDialog({
	type,
	onOpenChange,
	onSaved,
}: {
	type: AttachmentType
	onOpenChange: (o: boolean) => void
	onSaved: (updated?: AttachmentType) => void
}) {
	const [tab, setTab] = useState<EditTab>("info")

	return (
		<Dialog open onOpenChange={onOpenChange}>
			<DialogContent className="flex max-h-[90vh] flex-col sm:max-w-2xl">
				<DialogHeader className="shrink-0">
					<DialogTitle>Edit Attachment Type</DialogTitle>
					<p className="text-sm text-muted-foreground">{type.name}</p>
				</DialogHeader>

				{/* Tab bar */}
				<div className="flex gap-0 border-b shrink-0">
					{([
						{ id: "info" as EditTab, label: "Details", icon: Pencil },
						{ id: "pdf" as EditTab, label: "PDF Template", icon: Settings },
					] as const).map(({ id, label, icon: Icon }) => (
						<button
							key={id}
							type="button"
							onClick={() => setTab(id)}
							className={cn(
								"flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
								tab === id
									? "border-primary text-primary"
									: "border-transparent text-muted-foreground hover:text-foreground",
							)}
						>
							<Icon className="size-4" />
							{label}
						</button>
					))}
				</div>

				<div className="min-h-0 flex-1 overflow-y-auto">
					{tab === "info" && <InfoTab type={type} onSaved={onSaved} />}
					{tab === "pdf" && <PdfTemplateTab type={type} onSaved={onSaved} />}
				</div>
			</DialogContent>
		</Dialog>
	)
}

/* ─── Info Tab ───────────────────────────────────────────────────────────── */

function InfoTab({
	type,
	onSaved,
}: {
	type: AttachmentType
	onSaved: (updated?: AttachmentType) => void
}) {
	const update = trpc.attachmentType.update.useMutation({
		onSuccess: () => { toast.success("Type updated"); onSaved() },
		onError: (e) => toast.error(e.message),
	})

	return (
		<Form<TypeValues>
			schema={typeSchema}
			defaultValues={{ name: type.name, description: type.description ?? "" }}
			onSubmit={async (v) => {
				try {
					await update.mutateAsync({ id: type.id, name: v.name, description: v.description ?? null })
				} catch (e: any) {
					return e?.message ?? "An error occurred"
				}
			}}
			className="flex flex-col gap-4 pt-4"
		>
			<InputFormField name="name" label="Name" required />
			<TextareaFormField name="description" label="Description" />
			<DialogFooter>
				<Button type="submit" disabled={update.isPending}>
					{update.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
					Save
				</Button>
			</DialogFooter>
		</Form>
	)
}

/* ─── PDF Template Tab ───────────────────────────────────────────────────── */

function PdfTemplateTab({
	type,
	onSaved,
}: {
	type: AttachmentType
	onSaved: (updated?: AttachmentType) => void
}) {
	const utils = trpc.useUtils()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [diagSteps, setDiagSteps] = useState<Array<{ name: string; ok: boolean; detail: string }> | null>(null)

	const getUploadUrl = trpc.attachmentType.getPdfUploadUrl.useMutation()
	const updateType = trpc.attachmentType.update.useMutation({
		onSuccess: () => {
			utils.attachmentType.getAllByFilter.invalidate()
			toast.success("PDF template uploaded")
			onSaved()
		},
		onError: (e) => toast.error(e.message),
	})
	const testPdf = trpc.attachmentType.testPdf.useMutation({
		onSuccess: (data) => setDiagSteps(data.steps),
		onError: (e) => toast.error(e.message),
	})

	const getPdfInfo = trpc.attachmentType.getPdfInfo.useQuery(
		{ attachmentTypeId: type.id },
		{ enabled: false },
	)
	const getPdfFields = trpc.attachmentType.getPdfFields.useQuery(
		{ attachmentTypeId: type.id },
		{ enabled: false },
	)

	async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0]
		e.target.value = ""
		if (!file || file.type !== "application/pdf") {
			toast.error("Please select a PDF file")
			return
		}
		try {
			const { uploadUrl, s3Key } = await getUploadUrl.mutateAsync({
				attachmentTypeId: type.id,
				fileName: file.name,
			})
			const res = await fetch(uploadUrl, {
				method: "PUT",
				body: file,
				headers: { "Content-Type": "application/pdf" },
			})
			if (!res.ok) throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`)
			await updateType.mutateAsync({ id: type.id, pdfTemplateS3Key: s3Key } as any)
		} catch (err: any) {
			console.error("[pdf-upload] error:", err)
			toast.error(`Upload failed: ${err?.message ?? "please try again"}`)
		}
	}

	const hasTemplate = Boolean((type as any).pdfTemplateS3Key)
	const isUploading = getUploadUrl.isPending || updateType.isPending

	return (
		<div className="flex flex-col gap-6 pt-4 pb-2">
			<p className="text-sm text-muted-foreground">
				Upload a fillable PDF template for this document type. Once uploaded, use the tools below
				to inspect its AcroForm field names — you'll need those to wire up the fill mapping.
			</p>

			{/* Upload section */}
			<div className="flex items-center gap-3 flex-wrap">
				{hasTemplate ? (
					<>
						<div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
							<FileText className="w-4 h-4" />
							Template uploaded
						</div>
						<Button
							variant="outline"
							size="sm"
							onClick={() => fileInputRef.current?.click()}
							disabled={isUploading}
						>
							<Upload className="w-4 h-4 mr-1.5" />
							Replace PDF
						</Button>
					</>
				) : (
					<Button
						variant="secondary"
						onClick={() => fileInputRef.current?.click()}
						disabled={isUploading}
					>
						<Upload className="w-4 h-4 mr-1.5" />
						{isUploading ? "Uploading…" : "Upload PDF Template"}
					</Button>
				)}
				<input
					ref={fileInputRef}
					type="file"
					accept="application/pdf"
					className="hidden"
					onChange={handleFileChange}
				/>
			</div>

			{/* Inspect section */}
			{hasTemplate && (
				<div className="rounded-md border bg-muted/30 p-4 text-sm flex flex-col gap-4">
					{/* AcroForm fields */}
					<div className="flex flex-col gap-1.5">
						<p className="font-medium text-sm">AcroForm Field Names</p>
						<p className="text-xs text-muted-foreground">
							These are the exact field names used in the PDF. Copy them to build the fill mapping.
						</p>
						{getPdfFields.data ? (
							getPdfFields.data.error ? (
								<p className="text-xs text-destructive">{getPdfFields.data.error}</p>
							) : getPdfFields.data.fields.length === 0 ? (
								<p className="text-xs text-muted-foreground">
									No AcroForm fields detected — this is a flat or coordinate-based PDF.
								</p>
							) : (
								<ul className="text-xs font-mono text-muted-foreground space-y-0.5 max-h-56 overflow-y-auto rounded border bg-background p-2">
									{getPdfFields.data.fields.map((f) => (
										<li key={f.name}>
											<span className="text-foreground">{f.name}</span>{" "}
											<span className="opacity-50">({f.type})</span>
										</li>
									))}
								</ul>
							)
						) : (
							<Button
								variant="ghost"
								size="sm"
								className="self-start text-xs"
								onClick={() => getPdfFields.refetch()}
								disabled={getPdfFields.isFetching}
							>
								{getPdfFields.isFetching ? "Reading…" : "List PDF fields"}
							</Button>
						)}
					</div>

					{/* Page dimensions */}
					<div className="flex flex-col gap-1.5">
						<p className="font-medium text-sm">Page Dimensions</p>
						{getPdfInfo.data ? (
							getPdfInfo.data.error ? (
								<p className="text-xs text-destructive">{getPdfInfo.data.error}</p>
							) : (
								<ul className="text-xs font-mono text-muted-foreground space-y-0.5">
									{getPdfInfo.data.pages.map((p) => (
										<li key={p.page}>
											Page {p.page + 1}: {p.width.toFixed(1)} × {p.height.toFixed(1)} pt
										</li>
									))}
								</ul>
							)
						) : (
							<Button
								variant="ghost"
								size="sm"
								className="self-start text-xs"
								onClick={() => getPdfInfo.refetch()}
								disabled={getPdfInfo.isFetching}
							>
								{getPdfInfo.isFetching ? "Reading…" : "Show page dimensions"}
							</Button>
						)}
					</div>
				</div>
			)}

			{/* Diagnostics section */}
			<div className="rounded-md border p-4 flex flex-col gap-3">
				<p className="text-sm font-medium">Pipeline Diagnostics</p>
				<p className="text-xs text-muted-foreground">
					Verifies S3 access, parses the PDF, and lists all AcroForm fields. No data is written permanently.
				</p>
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="self-start"
					disabled={testPdf.isPending}
					onClick={() => {
						setDiagSteps(null)
						testPdf.mutate({ attachmentTypeId: type.id })
					}}
				>
					{testPdf.isPending ? "Running…" : "Run Diagnostic"}
				</Button>
				{diagSteps && (
					<ul className="flex flex-col gap-1.5 mt-1">
						{diagSteps.map((s, i) => (
							<li key={i} className="flex items-start gap-2 text-xs">
								{s.ok
									? <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" />
									: <XCircle className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />}
								<span>
									<span className="font-medium">{s.name}:</span>{" "}
									<span className={s.ok ? "text-muted-foreground" : "text-destructive"}>
										{s.detail}
									</span>
								</span>
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	)
}
